import styled from 'styled-components';

import { BasicSubtitle } from '../../../foundations/typefaces/BasicSubtitle';
import { SmallParagraph } from '../../../foundations/typefaces/SmallParagraph';
import type { Color, Size } from '../../../style/types/theme';
import { Size as CardButtonSize, Variant } from './types';

interface IconWrapperProps {
  $isMobile: boolean;
}

interface WrapperProps {
  $active: boolean;
  $disabled: boolean;
  $isMobile: boolean;

  $variant?: Variant;
  $size?: CardButtonSize;
}

interface GetColorProps {
  $active: boolean;
  $disabled: boolean;
  color: Color;
}

const getColor = ({ $active, $disabled, color }: GetColorProps) => {
  if ($active)
    return {
      base: color.content.accent.default(),
      hover: color.content.accent.hover(),
      border: color.border.accent.default(),
      borderHover: color.border.accent.default(),
    };
  if ($disabled)
    return {
      base: color.content.secondary.disabled(),
      hover: color.content.secondary.disabled(),
      border: color.border.primary.disabled(),
      borderHover: color.border.primary.disabled(),
    };

  return {
    base: color.content.secondary.default(),
    hover: color.content.secondary.hover(),
    border: color.border.primary.default(),
    borderHover: color.border.primary.hover(),
  };
};

interface GetWrapperGap {
  $variant: Variant;
  size: Size;
}

const getWrapperGap = ({ $variant, size }: GetWrapperGap) => {
  if ($variant === Variant.PADDED) return size.units(0.8);

  return size.units(0.5);
};

interface GetWrapperPadding {
  $variant: Variant;
  size: Size;
}

const getWrapperPadding = ({ $variant, size }: GetWrapperPadding) => {
  if ($variant === Variant.PADDED) return `${size.units(1)} ${size.units(1)}`;

  return `${size.units(1)} ${size.units(0.5)}`;
};

interface GetWrapperSize {
  $size: CardButtonSize;
  $isMobile: boolean;
  size: Size;
}

const getWrapperSize = ({ $size, $isMobile, size }: GetWrapperSize) => {
  if ($size === CardButtonSize.FIXED) {
    return {
      height: $isMobile ? size.units(10) : size.units(11),
      minWidth: $isMobile ? size.units(9) : size.units(10),
      maxWidth: $isMobile ? size.units(12.5) : size.units(15),
    };
  }

  if ($size === CardButtonSize.FULL) {
    return {
      width: '100%',
      height: $isMobile ? size.units(10) : size.units(11),
    };
  }

  return {};
};

export const IconWrapper = styled.div<IconWrapperProps>(
  ({ $isMobile, theme: { size } }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: $isMobile ? size.units(4.5) : size.units(5),
    height: $isMobile ? size.units(4.5) : size.units(5),
  }),
);

export const StyledIcon = styled.i(({ theme: { transition } }) => ({
  transition: transition.standard('color'),
}));

export const TextWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  gap: size.units(0.5),
}));

export const StyledBasicSubtitle = styled(BasicSubtitle)(({ theme: { transition } }) => ({
  textAlign: 'center',
  transition: transition.standard('color'),
}));

export const StyledText = styled(SmallParagraph)(({ theme: { transition } }) => ({
  textAlign: 'center',
  transition: transition.standard('color'),
}));

export const PremiumLabelWrapper = styled.div({});

export const Wrapper = styled.div<WrapperProps>(
  ({
    $active,
    $disabled,
    $isMobile,
    $variant = Variant.DEFAULT,
    $size = CardButtonSize.FIXED,
    theme: { color, border, size, transition },
  }) => ({
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    gap: getWrapperGap({ $variant, size }),
    justifyContent: 'flex-start',
    alignItems: 'center',
    border: border.small,
    borderColor: getColor({ $active, $disabled, color }).border,
    borderRadius: size.units(2),
    padding: getWrapperPadding({ $variant, size }),
    transition: transition.standard('border-color'),
    position: 'relative',

    ...getWrapperSize({ $size, $isMobile, size }),

    [StyledText]: {
      color: getColor({ $active, $disabled, color }).base,
    },

    [StyledBasicSubtitle]: {
      color: getColor({ $active, $disabled, color }).base,
    },

    [StyledIcon]: {
      color: getColor({ $active, $disabled, color }).base,
    },

    [PremiumLabelWrapper]: {
      position: 'absolute',
      top: size.units(0.5),
      right: size.units(0.5),
    },

    '&:hover': {
      borderColor: getColor({ $active, $disabled, color }).borderHover,

      [StyledText]: {
        color: getColor({ $active, $disabled, color }).hover,
      },

      [StyledIcon]: {
        color: getColor({ $active, $disabled, color }).hover,
      },
    },
  }),
);
